import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios";
import { toast } from "react-toastify";
import {
  FiSearch,
  FiRefreshCw,
  FiEye,
  FiExternalLink,
  FiDownload,
  FiCheckCircle,
  FiClock,
  FiUsers,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiPhone,
  FiMapPin,
  FiAlertCircle,
} from "react-icons/fi";
import "./ClaimedVendors.css";

const ClaimedVendors = () => {
  const navigate = useNavigate();

  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [stats, setStats] = useState({
    totalImported: 0,
    totalClaimed: 0,
    totalUnclaimed: 0,
    claimedToday: 0,
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  });

  // Filters
  const [search, setSearch] = useState("");
  const [claimStatus, setClaimStatus] = useState("claimed"); // Default to claimed
  const [stateFilter, setStateFilter] = useState("");

  const fetchClaimedVendors = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = {
          page,
          limit: pagination.limit,
          claimStatus,
        };
        if (search.trim()) params.search = search.trim();
        if (stateFilter) params.state = stateFilter;

        const res = await api.get("/admin/claimed-vendors", { params });

        if (res.data?.success) {
          setVendors(res.data.data || []);
          if (res.data.stats) {
            setStats(res.data.stats);
          }
          if (res.data.pagination) {
            setPagination((prev) => ({
              ...prev,
              page: res.data.pagination.page,
              total: res.data.pagination.total,
              totalPages: res.data.pagination.totalPages,
            }));
          }
        }
      } catch (err) {
        console.error("Error fetching claimed vendors:", err);
        toast.error(err.response?.data?.message || "Failed to fetch claimed vendors");
      } finally {
        setLoading(false);
      }
    },
    [search, claimStatus, stateFilter, pagination.limit]
  );

  useEffect(() => {
    fetchClaimedVendors(1);
  }, [fetchClaimedVendors]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page: newPage }));
      fetchClaimedVendors(newPage);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await api.get("/admin/claimed-vendors/export-csv", {
        params: {
          search: search.trim() || undefined,
          claimStatus,
          state: stateFilter || undefined,
        },
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `claimed-vendors-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("CSV export downloaded successfully!");
    } catch (err) {
      console.error("Error exporting CSV:", err);
      toast.error("Failed to export CSV file");
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setClaimStatus("claimed");
    setStateFilter("");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const statCardsData = [
    {
      label: "Total Claimed Profiles",
      value: stats.totalClaimed.toLocaleString("en-IN"),
      icon: <FiCheckCircle />,
      color: "#10b981",
    },
    {
      label: "Claimed Today",
      value: stats.claimedToday.toLocaleString("en-IN"),
      icon: <FiClock />,
      color: "#3b82f6",
    },
    {
      label: "Total Imported Vendors",
      value: stats.totalImported.toLocaleString("en-IN"),
      icon: <FiUsers />,
      color: "#6366f1",
    },
    {
      label: "Unclaimed Profiles",
      value: stats.totalUnclaimed.toLocaleString("en-IN"),
      icon: <FiAlertCircle />,
      color: "#f59e0b",
    },
  ];

  return (
    <div className="claimed-page">
      <div className="claimed-content">
        {/* Header */}
        <div className="claimed-header">
          <div className="claimed-header-left">
            <h1>
              <span>📋 Claimed Profiles (Imported Vendors)</span>
              <span className="claimed-header-badge">OTP Verified</span>
            </h1>
            <p className="claimed-subtitle">
              List of imported vendors who claimed their listing by verifying their mobile number via OTP.
            </p>
          </div>
          <div className="claimed-header-actions">
            <button
              className="claimed-btn-export"
              onClick={handleExportCSV}
              disabled={exporting}
              title="Download CSV report"
            >
              <FiDownload />
              <span>{exporting ? "Exporting..." : "Export CSV"}</span>
            </button>
            <button
              className={`claimed-refresh-btn ${loading ? "rotating" : ""}`}
              onClick={() => fetchClaimedVendors(pagination.page)}
              title="Refresh list"
            >
              <FiRefreshCw />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="claimed-stats">
          {statCardsData.map((stat, idx) => (
            <div
              key={idx}
              className="claimed-stat-card"
              style={{ "--stat-color": stat.color }}
            >
              <div className="claimed-stat-top">
                <span className="claimed-stat-label">{stat.label}</span>
                <span className="claimed-stat-icon">{stat.icon}</span>
              </div>
              <p className="claimed-stat-number">{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Main Section */}
        <div className="claimed-section">
          {/* Filters Bar */}
          <div className="claimed-filters-bar">
            <div className="claimed-filter-group">
              {/* Search */}
              <div className="claimed-search-box">
                <FiSearch className="claimed-search-icon" />
                <input
                  type="text"
                  placeholder="Search by business name, owner, phone, city..."
                  className="claimed-search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && fetchClaimedVendors(1)}
                />
              </div>

              {/* Claim Status Filter */}
              <select
                className="claimed-select"
                value={claimStatus}
                onChange={(e) => setClaimStatus(e.target.value)}
              >
                <option value="claimed">Status: Claimed Only (Verified)</option>
                <option value="all">Status: All Imported</option>
                <option value="unclaimed">Status: Unclaimed Only</option>
              </select>

              {/* State Filter */}
              <input
                type="text"
                placeholder="Filter by state..."
                className="claimed-select"
                style={{ width: "160px" }}
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchClaimedVendors(1)}
              />

              {(search || claimStatus !== "claimed" || stateFilter) && (
                <button className="claimed-clear-btn" onClick={clearFilters}>
                  <FiX /> Clear Filters
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="claimed-table-wrap">
            <table className="claimed-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Vendor / Business</th>
                  <th>Contact Details</th>
                  <th>Claim Status</th>
                  <th>Claimed Date & Time</th>
                  <th>Location</th>
                  <th style={{ textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: "center", padding: "40px" }}>
                      <FiRefreshCw className="rotating" style={{ fontSize: "24px", color: "#10b981" }} />
                      <p style={{ marginTop: "10px", color: "#64748b" }}>Loading claimed profiles...</p>
                    </td>
                  </tr>
                ) : vendors.length === 0 ? (
                  <tr>
                    <td colSpan="7">
                      <div className="claimed-empty">
                        <div className="claimed-empty-icon">📋</div>
                        <h4>No Vendors Found</h4>
                        <p>There are no imported vendors matching your current filter criteria.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  vendors.map((vendor, index) => {
                    const rowNumber = (pagination.page - 1) * pagination.limit + index + 1;
                    const isClaimed = Boolean(vendor.isClaimed);
                    const primaryPhone = vendor.claimedMobile || vendor.mobile_number;

                    return (
                      <tr key={vendor._id}>
                        {/* Index */}
                        <td style={{ color: "#94a3b8", fontWeight: 600, fontSize: "12.5px" }}>
                          {rowNumber}
                        </td>

                        {/* Business & Owner */}
                        <td>
                          <div className="claimed-business-name">
                            <span>{vendor.shop_name || vendor.name || "Vendor Listing"}</span>
                          </div>
                          {vendor.name && vendor.shop_name && vendor.name !== vendor.shop_name && (
                            <div className="claimed-owner-name">Owner: {vendor.name}</div>
                          )}
                          <div className="claimed-badges-row">
                            {vendor.business_category && (
                              <span className="claimed-pill">{vendor.business_category}</span>
                            )}
                            {vendor.isImported && (
                              <span className="claimed-pill" style={{ background: "#fef3c7", color: "#b45309" }}>
                                Imported
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Contact */}
                        <td>
                          <div style={{ fontWeight: 600, color: "#1e293b", display: "flex", alignItems: "center", gap: "5px" }}>
                            <FiPhone style={{ color: "#10b981", fontSize: "13px" }} />
                            {primaryPhone ? (
                              <a
                                href={`tel:${primaryPhone}`}
                                style={{ color: "#1e293b", textDecoration: "none" }}
                              >
                                {primaryPhone}
                              </a>
                            ) : (
                              "—"
                            )}
                          </div>
                          {isClaimed && (
                            <div className="badge-otp-verified">
                              <FiCheckCircle /> Mobile OTP Verified
                            </div>
                          )}
                          {vendor.email && (
                            <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                              {vendor.email}
                            </div>
                          )}
                        </td>

                        {/* Claim Status */}
                        <td>
                          {isClaimed ? (
                            <span className="badge-claimed">
                              <FiCheckCircle /> Profile Claimed
                            </span>
                          ) : (
                            <span className="badge-unclaimed">Unclaimed</span>
                          )}
                        </td>

                        {/* Claimed Date */}
                        <td style={{ color: isClaimed ? "#047857" : "#94a3b8", fontWeight: isClaimed ? 600 : 400, fontSize: "12.5px" }}>
                          {isClaimed ? formatDate(vendor.claimedAt || vendor.updatedAt) : "Not Claimed Yet"}
                        </td>

                        {/* Location */}
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "13px", color: "#334155" }}>
                            <FiMapPin style={{ color: "#64748b" }} />
                            <span>
                              {vendor.city ? vendor.city.charAt(0).toUpperCase() + vendor.city.slice(1) : ""}
                              {vendor.city && vendor.state ? ", " : ""}
                              {vendor.state || (!vendor.city ? "—" : "")}
                            </span>
                          </div>
                        </td>

                        {/* Actions */}
                        <td style={{ textAlign: "center" }}>
                          <div className="claimed-actions-group" style={{ justifyContent: "center" }}>
                            <button
                              type="button"
                              className="claimed-btn-view"
                              onClick={() => navigate(`/vendor-details/${vendor._id}`)}
                              title="View Vendor Details"
                            >
                              <FiEye /> View
                            </button>
                            {vendor.profile_slug && (
                              <a
                                href={`https://bandhanguru.com/vendor-description/${vendor.profile_slug}`}
                                target="_blank"
                                rel="noreferrer"
                                className="claimed-btn-link"
                                title="Open Live Profile"
                              >
                                <FiExternalLink /> Live
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="claimed-pagination">
              <span>
                Showing page <strong>{pagination.page}</strong> of <strong>{pagination.totalPages}</strong> ({pagination.total} total vendors)
              </span>
              <div className="claimed-page-buttons">
                <button
                  className="claimed-page-btn"
                  disabled={pagination.page <= 1}
                  onClick={() => handlePageChange(pagination.page - 1)}
                >
                  <FiChevronLeft /> Prev
                </button>
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                  .filter(
                    (p) =>
                      p === 1 ||
                      p === pagination.totalPages ||
                      Math.abs(p - pagination.page) <= 1
                  )
                  .map((p, idx, arr) => (
                    <React.Fragment key={p}>
                      {idx > 0 && arr[idx - 1] !== p - 1 && (
                        <span style={{ padding: "4px 8px", color: "#94a3b8" }}>...</span>
                      )}
                      <button
                        className={`claimed-page-btn ${pagination.page === p ? "active" : ""}`}
                        onClick={() => handlePageChange(p)}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  ))}
                <button
                  className="claimed-page-btn"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => handlePageChange(pagination.page + 1)}
                >
                  Next <FiChevronRight />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClaimedVendors;
